import React, { useEffect, useState } from 'react';
import { Outlet, useParams, NavLink } from 'react-router-dom';
import { useStore } from '../../store';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Zap, LayoutDashboard, Plus, Sun, Moon, LogOut,
  ChevronDown, ChevronRight, Users2, Menu, X, CheckSquare, Settings, X as XIcon, Folder, Edit2, Trash2
} from 'lucide-react';
import CreateProjectModal from '../Projects/CreateProjectModal';
import EditProjectModal from '../Projects/EditProjectModal';
import CreateGroupModal from '../Projects/CreateGroupModal';
import EditGroupModal from '../Projects/EditGroupModal';
import Avatar from '../UI/Avatar';

function EditProfileModal({ onClose }) {
  const { user, updateMe } = useStore();
  const [name, setName] = useState(user?.name || '');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await updateMe({ name: name.trim() });
      onClose();
    } catch (err) {
      alert(err.message || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm">Редактировать профиль</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label text-xs">Имя</label>
            <input
              className="input text-sm"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ваше имя"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary text-xs py-1.5">Отмена</button>
            <button type="submit" disabled={loading || !name.trim()} className="btn-primary text-xs py-1.5">
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SidebarContent({ onClose, onCreateProject, onEditProject, onCreateGroup, onEditGroup }) {
  const { user, projects, projectGroups, theme, setTheme, logout } = useStore();
  const { canProjectEdit } = usePermissions();
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showEditProfile, setShowEditProfile] = useState(false);
  const nav = () => { if (onClose) onClose(); };

  const getGroupId = (p) => (typeof p.group === 'string' ? p.group : p.group?._id || null);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
        <div className="w-7 h-7 rounded-lg bg-brand-600 flex items-center justify-center">
          <Zap className="w-4 h-4 text-white" />
        </div>
        <span className="font-bold text-sm flex-1">TrackerTurtton</span>
        {onClose && (
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 md:hidden">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
        <NavLink to="/" end onClick={nav} className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <LayoutDashboard className="w-4 h-4 flex-shrink-0" /> Обзор
        </NavLink>
        <NavLink to="/workload" onClick={nav} className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <Users2 className="w-4 h-4 flex-shrink-0" /> Загруженность
        </NavLink>
        <NavLink to="/my-tasks" onClick={nav} className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <CheckSquare className="w-4 h-4 flex-shrink-0" /> Мои задачи
        </NavLink>

        <div className="pt-3 pb-1 px-3">
          <div className="flex items-center justify-between">
            <button onClick={() => setProjectsOpen(p => !p)}
              className="flex items-center gap-1 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              {projectsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Проекты
            </button>
            {user && (
              <div className="flex items-center gap-0.5">
                <button onClick={onCreateGroup} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Создать группу">
                  <Folder className="w-3.5 h-3.5" />
                </button>
                <button onClick={onCreateProject} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Создать проект">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
        {projectsOpen && (
          <>
            {/* Groups with projects */}
            {projectGroups.map(group => {
              const groupProjects = projects.filter(p => getGroupId(p) === group._id);
              if (groupProjects.length === 0) return null;
              const isExpanded = expandedGroups[group._id] !== false;

              return (
                <div key={group._id} className="mb-1">
                  <button onClick={() => toggleGroup(group._id)}
                    className="w-full flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    style={{ backgroundColor: isExpanded ? `${group.color}20` : 'transparent' }}>
                    {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <span className="text-base">{group.icon}</span>
                    <span className="flex-1 text-left truncate">{group.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); onEditGroup?.(group); }}
                      className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-all">
                      <Edit2 className="w-3 h-3 text-gray-400" />
                    </button>
                  </button>
                  {isExpanded && (
                    <div className="ml-4">
                      {groupProjects.map(p => (
                        <div key={p._id} className="group flex items-center">
                          <NavLink to={`/project/${p._id}`} onClick={nav}
                            className={({ isActive }) => `sidebar-item flex-1 ${isActive ? 'active' : ''} flex justify-between items-center w-full`}>
                            <div className="gap-1 flex items-center">
                              <span className="text-base leading-none flex-shrink-0">{p.icon || '📋'}</span>
                              <span className="truncate">{p.name}</span>
                            </div>
                            {canProjectEdit(p) && onEditProject && (
                              <button onClick={(e) => { e.preventDefault(); onEditProject(p); }}
                                className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-all">
                                <Settings className="w-3.5 h-3.5 text-gray-400" />
                              </button>
                            )}
                          </NavLink>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Ungrouped projects (rendered plainly, without a "Без группы" header) */}
            {projects.filter(p => !getGroupId(p)).map(p => (
              <div key={p._id} className="group flex items-center">
                <NavLink to={`/project/${p._id}`} onClick={nav}
                  className={({ isActive }) => `sidebar-item flex-1 ${isActive ? 'active' : ''} flex justify-between items-center w-full`}>
                  <div className="gap-1 flex items-center">
                    <span className="text-base leading-none flex-shrink-0">{p.icon || '📋'}</span>
                    <span className="truncate">{p.name}</span>
                  </div>
                  {canProjectEdit(p) && onEditProject && (
                    <button onClick={(e) => { e.preventDefault(); onEditProject(p); }}
                      className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-all">
                      <Settings className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                  )}
                </NavLink>
              </div>
            ))}

            {projects.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">Нет проектов</div>
            )}
          </>
        )}
      </nav>
      <div className="p-2 border-t border-gray-100 dark:border-gray-800 space-y-0.5 flex-shrink-0">
        <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="sidebar-item w-full">
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
        </button>
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            onClick={() => setShowEditProfile(true)}
            className="flex items-center gap-2 flex-1 min-w-0 text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg px-1 py-0.5 -ml-1 transition-colors"
          >
            <Avatar name={user?.name} color={user?.color} src={user?.avatar} size="sm" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user?.name}</div>
              {user?.authProvider === 'google' && <div className="text-[10px] text-gray-400">via Google</div>}
            </div>
          </button>
          <button onClick={logout} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
        {showEditProfile && <EditProfileModal onClose={() => setShowEditProfile(false)} />}
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { fetchProjects, setActiveProject, projects, projectGroups, fetchProjectGroups } = useStore();
  const { id } = useParams();
  const [showCreate, setShowCreate] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [createGroup, setCreateGroup] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { fetchProjects(); }, []);
  useEffect(() => {
    if (id && projects.length) {
      const p = projects.find(x => x._id === id);
      if (p) setActiveProject(p);
    }
  }, [id, projects]);
  useEffect(() => { setMobileOpen(false); }, [id]);

  const ensureGroupsLoaded = () => {
    if (projectGroups.length === 0) {
      fetchProjectGroups();
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-950">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-56 lg:w-60 flex-shrink-0 flex-col border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <SidebarContent
          onCreateProject={() => setShowCreate(true)}
          onEditProject={setEditProject}
          onCreateGroup={() => { ensureGroupsLoaded(); setCreateGroup(true); }}
          onEditGroup={setEditGroup}
        />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white dark:bg-gray-900 shadow-2xl flex flex-col z-50">
            <SidebarContent
              onClose={() => setMobileOpen(false)}
              onCreateProject={() => { setShowCreate(true); setMobileOpen(false); }}
              onEditProject={(p) => { setEditProject(p); setMobileOpen(false); }}
              onCreateGroup={() => { ensureGroupsLoaded(); setCreateGroup(true); setMobileOpen(false); }}
              onEditGroup={(g) => { setEditGroup(g); setMobileOpen(false); }}
            />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile topbar */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <button onClick={() => setMobileOpen(true)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500">
            <Menu className="w-5 h-5" />
          </button>
          <div className="w-6 h-6 rounded-md bg-brand-600 flex items-center justify-center">
            <Zap className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-bold text-sm">TrackerTurtton</span>
        </div>
        <main className="flex-1 overflow-hidden flex flex-col">
          <Outlet />
        </main>
      </div>
      {showCreate && <CreateProjectModal onClose={() => setShowCreate(false)} />}
      {editProject && <EditProjectModal project={editProject} onClose={() => setEditProject(null)} />}
      {createGroup && <CreateGroupModal onClose={() => setCreateGroup(false)} projects={projects} />}
      {editGroup && <EditGroupModal group={editGroup} onClose={() => setEditGroup(null)} />}
    </div>
  );
}
